import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import JsonLd from '@/components/base/JsonLd';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://oopbuyspread.pics';
const LOGO_URL = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg';

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'OOPBuy Einkaufsführer Für Anfänger: Schritt Für Schritt Zum Ersten Kauf',
  description: 'Der perfekte Einstieg für Neueinsteiger. Vom ersten Spreadsheet-Blick über die Produktauswahl bis zur ersten Bestellung - einfach und verständlich erklärt.',
  author: {
    '@type': 'Organization',
    name: 'OOPBuy Editorial Team',
    url: SITE_URL,
  },
  publisher: {
    '@type': 'Organization',
    name: 'OOPBuy Spreadsheet',
    url: SITE_URL,
    logo: { '@type': 'ImageObject', url: LOGO_URL },
  },
  datePublished: '2026-07-15',
  dateModified: '2026-07-15',
  mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/artikel/oopbuy-einkaufsfuehrer` },
  inLanguage: 'de',
};

const howToJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'OOPBuy Einkaufsführer: Schritt Für Schritt Zum Ersten Kauf',
  description: 'Der perfekte Einstieg für Neueinsteiger. Vom ersten Spreadsheet-Blick bis zur ersten Bestellung.',
  step: [
    { '@type': 'HowToStep', position: 1, name: 'Kategorie Auswählen', text: 'Öffne das Spreadsheet und wähle eine der 14 Kategorien. Für den ersten Einkauf empfehlen wir Shoes oder T-Shirts.' },
    { '@type': 'HowToStep', position: 2, name: 'Verkäufer Vergleichen', text: 'Vergleiche Preise, Bewertungen und Versandoptionen in jeder Kategorie.' },
    { '@type': 'HowToStep', position: 3, name: 'Produkt Auswählen', text: 'Wähle das gewünschte Produkt aus und achte auf Größenangaben, Farboptionen und Varianten.' },
    { '@type': 'HowToStep', position: 4, name: 'QC-Bilder Anfordern', text: 'Vor dem Kauf QC-Bilder anfordern, um den tatsächlichen Zustand des Produkts zu überprüfen.' },
    { '@type': 'HowToStep', position: 5, name: 'Versand Wählen', text: 'Wähle die passende Versandoption - Standard ist günstiger, Express schneller.' },
    { '@type': 'HowToStep', position: 6, name: 'Bezahlen & Warten', text: 'Bezahle mit deiner bevorzugten Methode und verfolge deine Bestellung mit der Tracking-Nummer.' },
  ],
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { '@type': 'Question', name: 'Muss ich mich registrieren um das Spreadsheet zu nutzen?', acceptedAnswer: { '@type': 'Answer', text: 'Nein, das OOPBuy Spreadsheet ist ohne Registrierung zugänglich. Du kannst alle 14 Kategorien frei durchstöbern.' } },
    { '@type': 'Question', name: 'Wie finde ich die besten Angebote?', acceptedAnswer: { '@type': 'Answer', text: 'Das Spreadsheet ist nach Beliebtheit und Bewertung sortiert. Achte auf das Verified-Siegel und hohe Bewertungen.' } },
    { '@type': 'Question', name: 'Was mache ich bei Problemen mit der Bestellung?', acceptedAnswer: { '@type': 'Answer', text: 'Kontaktiere zuerst den Verkäufer. Bei unlösbaren Problemen hilft die Buyer Protection weiter.' } },
    { '@type': 'Question', name: 'Kann ich das Spreadsheet auch auf dem Handy nutzen?', acceptedAnswer: { '@type': 'Answer', text: 'Ja, das OOPBuy Spreadsheet ist vollständig für mobile Geräte optimiert.' } },
  ],
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Startseite', item: SITE_URL },
    { '@type': 'ListItem', position: 2, name: 'OOPBuy Einkaufsführer', item: `${SITE_URL}/artikel/oopbuy-einkaufsfuehrer` },
  ],
};

export default function EinkaufsfuehrerPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const currentDate = '2026-07-15';

  return (
    <>
      <Navbar />
      <JsonLd data={articleJsonLd} />
      <JsonLd data={howToJsonLd} />
      <JsonLd data={faqJsonLd} />
      <JsonLd data={breadcrumbJsonLd} />
      <article className="min-h-screen bg-background-50 mt-16 md:mt-[4.5rem]">
        <header className="w-full bg-background-100 py-16 md:py-20">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 font-[family-name:var(--font-label)]" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-foreground-800 transition-colors cursor-pointer">Startseite</Link>
              <i className="ri-arrow-right-s-line text-sm"></i>
              <span className="text-foreground-800">OOPBuy Einkaufsführer</span>
            </nav>
            <p className="text-xs uppercase tracking-[0.2em] text-accent-600 mb-3 font-[family-name:var(--font-label)]">Schritt Für Schritt</p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-6">
              OOPBuy Einkaufsführer Für Anfänger: Schritt Für Schritt Zum Ersten Kauf
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl font-[family-name:var(--font-body)]">
              Der perfekte Einstieg für Neueinsteiger. Vom ersten Spreadsheet-Blick über die Produktauswahl bis zur ersten Bestellung - einfach und verständlich erklärt.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-6 pt-6 border-t border-background-200">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-user-line text-sm text-accent-600"></i>
                </div>
                <span className="text-xs text-foreground-600 font-[family-name:var(--font-label)]">OOPBuy Editorial Team</span>
              </div>
              <span className="text-xs text-foreground-500 font-[family-name:var(--font-label)]">Veröffentlicht: {currentDate}</span>
              <span className="text-xs text-foreground-500 font-[family-name:var(--font-label)]">Lesezeit: 8 Minuten</span>
            </div>
          </div>
        </header>

        <nav className="w-full max-w-3xl mx-auto px-4 md:px-6 py-8" aria-label="Inhaltsverzeichnis">
          <div className="bg-background-100 rounded-xl p-6">
            <h2 className="text-sm font-semibold text-foreground-800 mb-4 font-[family-name:var(--font-label)]">Inhaltsverzeichnis</h2>
            <ul className="space-y-2">
              {tocLabels.map((label, i) => (
                <li key={tocIds[i]}><a href={`#${tocIds[i]}`} className="text-sm text-foreground-600 hover:text-accent-600 transition-colors cursor-pointer font-[family-name:var(--font-body)]">{i + 1}. {label}</a></li>
              ))}
            </ul>
          </div>
        </nav>

        <div className="w-full max-w-3xl mx-auto px-4 md:px-6 pb-16 md:pb-20">
          <div className="prose-custom space-y-8 text-sm md:text-base text-foreground-700 leading-relaxed font-[family-name:var(--font-body)]">

            <section id="einleitung">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">Einleitung: Dein Erster Einkauf Mit OOPBuy</h2>
              <p>Herzlich willkommen beim <strong>OOPBuy Spreadsheet</strong> Einkaufsführer! Dieser Guide wurde speziell für Anfänger geschrieben, die noch nie international eingekauft haben. Keine Sorge - wir führen dich Schritt für Schritt durch den gesamten Prozess, von der ersten Nutzung des Spreadsheets bis zum Auspacken deiner Bestellung.</p>
              <p>Das <strong>OOPBuy Spreadsheet</strong> macht den Einstieg besonders einfach, da alle Informationen übersichtlich in 14 Kategorien strukturiert sind. Du musst kein Experte sein - folge einfach dieser Anleitung und dein erster Einkauf wird ein voller Erfolg.</p>
            </section>

            <section id="spreadsheet-erklaert">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">Das OOPBuy Spreadsheet Verstehen</h2>
              <p>Bevor du mit dem Einkaufen beginnst, lohnt sich ein kurzer Blick auf die Struktur des <strong>Spreadsheets</strong>. Es ist in 14 Kategorien unterteilt - von <strong>Shoes</strong> über <strong>Hoodies</strong> bis zu <strong>Accessories</strong>. Jede Kategorie enthält verifizierte Verkäufer mit aktuellen Preisen und detaillierten Produktinformationen.</p>
              <p>Die wichtigsten Elemente im <strong>OOPBuy Spreadsheet Deutschland</strong> auf einen Blick: Kategoriename, Verkäuferbewertung, Preis in Euro, Versandoptionen und QC-Verfügbarkeit. Diese Informationen helfen dir, schnell die besten Angebote zu finden.</p>
            </section>

            <section id="schritt-fuer-schritt">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">Schritt Für Schritt Zur Ersten Bestellung</h2>
              <div className="space-y-5 my-6">
                {steps.map((step, i) => (
                  <div key={i} className="flex gap-4 p-5 rounded-xl bg-background-100">
                    <div className="w-12 h-12 rounded-full bg-accent-500 text-foreground-950 flex items-center justify-center flex-shrink-0 text-lg font-bold font-[family-name:var(--font-label)]">{step.num}</div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-800 mb-2 font-[family-name:var(--font-label)]">{step.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section id="tipps">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">Tipps Für Erfolgreiche Einkäufe</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                {tips.map((tip, i) => (
                  <div key={i} className="p-4 rounded-xl bg-accent-50 border border-accent-200 flex items-start gap-3">
                    <i className={`${tip.icon} text-accent-500 mt-0.5 text-lg`}></i>
                    <div>
                      <span className="text-sm font-semibold text-foreground-800 font-[family-name:var(--font-label)]">{tip.title}</span>
                      <p className="text-xs text-foreground-500 mt-1">{tip.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section id="fehler">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">Häufige Anfängerfehler Vermeiden</h2>
              <p>Viele Anfänger machen die gleichen Fehler. Mit dem <strong>OOPBuy Spreadsheet</strong> kannst du diese vermeiden. Der häufigste Fehler: ohne QC-Bilder zu bestellen. Besonders bei teuren Artikeln wie <strong>Designer-Sneakern</strong> oder <strong>Markenjacken</strong> sind QC-Bilder unverzichtbar.</p>
              <p>Ein weiterer typischer Fehler ist die falsche Einschätzung der Versandkosten. Das <strong>Spreadsheet</strong> zeigt dir vorab die geschätzten Versandkosten an. Plane diese in dein Budget ein, um böse Überraschungen zu vermeiden. Auch das Ignorieren der Zollfreigrenzen kann teuer werden - informiere dich vorab.</p>
            </section>

            <section id="faq">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">Häufig Gestellte Fragen Von Anfängern</h2>
              <div className="space-y-4">
                {faqItems.map((faq, i) => (
                  <details key={i} className="group bg-background-100 rounded-xl overflow-hidden">
                    <summary className="flex items-center justify-between p-4 cursor-pointer font-[family-name:var(--font-label)]">
                      <span className="text-sm font-semibold text-foreground-800 pr-4">{faq.q}</span>
                      <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                    </summary>
                    <div className="px-4 pb-4"><p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p></div>
                  </details>
                ))}
              </div>
            </section>

            <section id="fazit">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">Fazit: Dein Erster Einkauf Wartet</h2>
              <p>Mit diesem Einkaufsführer und dem <strong>OOPBuy Spreadsheet</strong> bist du bestens für deinen ersten internationalen Einkauf gerüstet. Die Kombination aus strukturierten Kategorien, verifizierten Verkäufern und transparenten Versandinformationen macht den Einstieg einfach und sicher.</p>
              <p>Zögere nicht länger - öffne das <strong>OOPBuy Spreadsheet Deutschland</strong>, wähle deine erste Kategorie und starte dein Shopping-Abenteuer. Bei Fragen hilft dir unser Support-Team jederzeit gerne weiter.</p>
            </section>

            <div className="mt-12 p-8 rounded-2xl bg-accent-500 text-center">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-3">Bereit Für Deinen Ersten Einkauf?</h2>
              <p className="text-sm text-foreground-800 mb-6 font-[family-name:var(--font-body)]">Starte jetzt mit dem OOPBuy Spreadsheet und entdecke die besten Angebote.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center px-8 py-3.5 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-foreground-800 transition-colors font-[family-name:var(--font-label)]">Buy It Now<i className="ri-arrow-right-line ml-2"></i></a>
                <Link to="/" className="inline-flex items-center px-6 py-3.5 rounded-full border border-foreground-900/30 text-foreground-800 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-accent-600 transition-colors font-[family-name:var(--font-label)]">Zurück Zur Startseite</Link>
              </div>
            </div>

            <div className="mt-12 p-6 rounded-xl bg-background-100">
              <p className="text-xs text-foreground-500 mb-3 font-[family-name:var(--font-label)]">Das könnte dich auch interessieren:</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/" className="text-sm text-accent-600 hover:text-accent-700 transition-colors cursor-pointer font-[family-name:var(--font-body)]">← OOPBuy Spreadsheet Startseite</Link>
                <Link to="/artikel/oopbuy-spreadsheet-ratgeber" className="text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer font-[family-name:var(--font-body)]">OOPBuy Spreadsheet Ratgeber</Link>
                <Link to="/artikel/oopbuy-versand" className="text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer font-[family-name:var(--font-body)]">Versandguide</Link>
                <Link to="/artikel/oopbuy-sicherheit" className="text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer font-[family-name:var(--font-body)]">Sicherheitsguide</Link>
              </div>
            </div>

            <div className="mt-8 p-4 rounded-lg border border-background-200">
              <p className="text-xs text-foreground-400 leading-relaxed font-[family-name:var(--font-body)]">
                <strong>Haftungsausschluss:</strong> Dieser Einkaufsführer enthält allgemeine Empfehlungen. Preise und Verfügbarkeiten können sich ändern. Überprüfe vor jedem Kauf die aktuellen Angaben im Spreadsheet.
              </p>
            </div>
          </div>
        </div>

        <footer className="w-full bg-accent-900">
          <div className="w-full max-w-6xl mx-auto px-4 md:px-6 py-12">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <Link to="/" className="text-sm text-foreground-300 hover:text-background-50 transition-colors cursor-pointer font-[family-name:var(--font-body)]">← Zurück zur OOPBuy Spreadsheet Startseite</Link>
              <p className="text-xs text-foreground-400 font-[family-name:var(--font-body)]">&copy; {new Date().getFullYear()} OOPBuy Spreadsheet. Alle Rechte vorbehalten.</p>
            </div>
          </div>
        </footer>
      </article>
    </>
  );
}

const tocIds = ['einleitung','spreadsheet-erklaert','schritt-fuer-schritt','tipps','fehler','faq','fazit'];
const tocLabels = ['Einleitung','Das Spreadsheet Verstehen','Schritt Für Schritt','Tipps Für Erfolgreiche Einkäufe','Häufige Anfängerfehler','FAQ','Fazit'];

const steps = [
  { num: 1, title: 'Kategorie Auswählen', desc: 'Öffne das Spreadsheet und wähle eine der 14 Kategorien. Für den ersten Einkauf empfehlen wir Shoes oder T-Shirts - diese Kategorien haben die meisten verifizierten Verkäufer.' },
  { num: 2, title: 'Verkäufer Vergleichen', desc: 'In jeder Kategorie findest du mehrere Verkäufer. Vergleiche Preise, Bewertungen und Versandoptionen. Das Spreadsheet zeigt dir die wichtigsten Kennzahlen auf einen Blick.' },
  { num: 3, title: 'Produkt Auswählen', desc: 'Hast du einen Verkäufer gefunden, wähle das gewünschte Produkt aus. Achte auf Größenangaben, Farboptionen und eventuelle Varianten.' },
  { num: 4, title: 'QC-Bilder Anfordern', desc: 'Vor dem endgültigen Kauf empfehlen wir, QC-Bilder anzufordern. Diese zeigen den tatsächlichen Zustand des Produkts und helfen dir, Mängel zu erkennen, bevor die Ware versendet wird.' },
  { num: 5, title: 'Versand Wählen', desc: 'Wähle die passende Versandoption. Standard ist günstiger, Express schneller. Das Spreadsheet zeigt dir die voraussichtlichen Kosten und Lieferzeiten für deine Region an.' },
  { num: 6, title: 'Bezahlen & Warten', desc: 'Bezahle mit deiner bevorzugten Methode und verfolge deine Bestellung mit der Tracking-Nummer. Das Spreadsheet benachrichtigt dich bei Statusänderungen.' },
];

const tips = [
  { icon: 'ri-star-line', title: 'Klein Anfangen', desc: 'Starte mit einer kleinen Bestellung, um den Prozess kennenzulernen. So minimierst du das Risiko bei deinem ersten Einkauf.' },
  { icon: 'ri-calendar-check-line', title: 'Geduld Haben', desc: 'Internationaler Versand braucht Zeit. Plane 2-3 Wochen ein und bestelle nicht auf den letzten Drücker.' },
  { icon: 'ri-camera-line', title: 'Immer QC-Bilder', desc: 'Verzichte nie auf QC-Bilder, besonders bei teuren Artikeln. Diese 1-2 Tage extra lohnen sich immer.' },
  { icon: 'ri-calculator-line', title: 'Budget Planen', desc: 'Kalkuliere Versandkosten und mögliche Zollgebühren von Anfang an ein, um dein Budget nicht zu sprengen.' },
];

const faqItems = [
  { q: 'Muss ich mich registrieren um das Spreadsheet zu nutzen?', a: 'Nein, das OOPBuy Spreadsheet ist ohne Registrierung zugänglich. Du kannst alle 14 Kategorien frei durchstöbern und direkt zu den Angeboten navigieren.' },
  { q: 'Wie finde ich die besten Angebote?', a: 'Das Spreadsheet ist nach Beliebtheit und Bewertung sortiert. Achte auf das Verified-Siegel und hohe Bewertungen für die besten Angebote.' },
  { q: 'Was mache ich bei Problemen mit der Bestellung?', a: 'Kontaktiere zuerst den Verkäufer über die im Spreadsheet hinterlegten Kontaktdaten. Bei unlösbaren Problemen hilft dir die Buyer Protection weiter.' },
  { q: 'Kann ich das Spreadsheet auch auf dem Handy nutzen?', a: 'Ja, das OOPBuy Spreadsheet ist vollständig für mobile Geräte optimiert. Du kannst auch unterwegs bequem einkaufen.' },
];